import React, { useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../shared/api';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';
import { CenteredLoader } from '../../components/LoadingSpinner';

const SystemSettings = () => {
  const { t } = useTranslation();
  const [settingsId, setSettingsId] = useState(null);
  const [formData, setFormData] = useState({
    auto_backup_enabled: false,
    backup_frequency_days: 7,
    maintenance_mode: false,
    enable_barcode_scanning: true,
    enable_qr_codes: true,
    enable_photo_uploads: true,
    enable_damage_tracking: true,
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const { isLoading: loading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const response = await api.get('/settings/api/system/');
      const data = Array.isArray(response) ? response[0] : response;
      if (data) {
        setSettingsId(data.id);
        setFormData({
          auto_backup_enabled: data.auto_backup_enabled ?? false,
          backup_frequency_days: data.backup_frequency_days ?? 7,
          maintenance_mode: data.maintenance_mode ?? false,
          enable_barcode_scanning: data.enable_barcode_scanning ?? true,
          enable_qr_codes: data.enable_qr_codes ?? true,
          enable_photo_uploads: data.enable_photo_uploads ?? true,
          enable_damage_tracking: data.enable_damage_tracking ?? true,
        });
      }
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => settingsId
      ? api.patch(`/settings/api/system/${settingsId}/`, payload)
      : api.post('/settings/api/system/', payload),
    onSuccess: () => setSuccess(t('settings.system.success.updated')),
    onError: () => setError(t('settings.system.errors.saveFailed')),
  });
  const saving = saveMutation.isPending;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    saveMutation.mutate(formData);
  };

  if (loading) {
    return (
      <PageShell pretitle={t('menu.settings')} title={t('settings.system.title')}>
        <CenteredLoader />
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('menu.settings')}
      title={t('settings.system.title')}
      actions={
        <AppButton variant="primary" onClick={handleSave} loading={saving} loadingText={t('settings.system.saving')}>
          {t('settings.system.save')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <Form onSubmit={handleSave}>
        <Row className="row-cards">
          <Col md={6}>
            <PageSection title={t('settings.system.backupSettings')}>
                    <div className="mb-3">
                      <Form.Check
                        type="switch"
                        id="auto_backup_enabled"
                        name="auto_backup_enabled"
                        label={t('settings.system.autoBackup')}
                        checked={formData.auto_backup_enabled}
                        onChange={handleChange}
                      />
                    </div>
                    <Form.Group>
                      <Form.Label>{t('settings.system.backupFrequency')}</Form.Label>
                      <Form.Control
                        type="number"
                        name="backup_frequency_days"
                        value={formData.backup_frequency_days}
                        onChange={handleChange}
                        min={1}
                        max={365}
                        disabled={!formData.auto_backup_enabled}
                        style={{ maxWidth: '120px' }}
                      />
                    </Form.Group>
            </PageSection>

            <PageSection title={t('settings.system.maintenance')} className="mt-3">
                    <Form.Check
                      type="switch"
                      id="maintenance_mode"
                      name="maintenance_mode"
                      label={t('settings.system.maintenanceMode')}
                      checked={formData.maintenance_mode}
                      onChange={handleChange}
                    />
                    <div className="text-muted small mt-1">
                      {t('settings.system.maintenanceModeHelp')}
                    </div>
            </PageSection>
          </Col>

          <Col md={6}>
            <PageSection title={t('settings.system.featureFlags')}>
                    <div className="mb-3">
                      <Form.Check
                        type="switch"
                        id="enable_barcode_scanning"
                        name="enable_barcode_scanning"
                        label={t('settings.system.barcodeScanning')}
                        checked={formData.enable_barcode_scanning}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <Form.Check
                        type="switch"
                        id="enable_qr_codes"
                        name="enable_qr_codes"
                        label={t('settings.system.qrCodes')}
                        checked={formData.enable_qr_codes}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <Form.Check
                        type="switch"
                        id="enable_photo_uploads"
                        name="enable_photo_uploads"
                        label={t('settings.system.photoUploads')}
                        checked={formData.enable_photo_uploads}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Form.Check
                        type="switch"
                        id="enable_damage_tracking"
                        name="enable_damage_tracking"
                        label={t('settings.system.damageTracking')}
                        checked={formData.enable_damage_tracking}
                        onChange={handleChange}
                      />
                    </div>
            </PageSection>
          </Col>
        </Row>
      </Form>
    </PageShell>
  );
};

export default SystemSettings;
